import React, { useState, useRef, useEffect } from 'react';
import styles from '../styles/components/ColorPicker.module.scss';

const ColorPicker = ({
    color,
    onChange,
    label,
    disabled = false
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [tempColor, setTempColor] = useState(color);
    const pickerRef = useRef(null);
    const inputRef = useRef(null);

    // 外部クリックでピッカーを閉じる
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (pickerRef.current && !pickerRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);

    // propsのcolorが変更されたときにtempColorを更新
    useEffect(() => {
        setTempColor(color);
    }, [color]);

    const handleColorChange = (newColor) => {
        setTempColor(newColor);
        onChange(newColor);
    };

    const handleInputChange = (e) => {
        const newColor = e.target.value;
        handleColorChange(newColor);
    };

    const handleSwatchClick = () => {
        if (!disabled) {
            setIsOpen(!isOpen);
        }
    };

    // よく使われる色のプリセット
    const presetColors = [
        '#FF0000', '#FF8000', '#FFFF00', '#80FF00', '#00FF00', '#00FF80',
        '#00FFFF', '#0080FF', '#0000FF', '#8000FF', '#FF00FF', '#FF0080',
        '#800000', '#804000', '#808000', '#408000', '#008000', '#008040',
        '#008080', '#004080', '#000080', '#400080', '#800080', '#800040',
        '#000000', '#404040', '#808080', '#C0C0C0', '#FFFFFF', '#FFE0E0',
        '#FFE0C0', '#FFFFE0', '#E0FFE0', '#E0FFFF', '#E0E0FF', '#F0E0FF'
    ];

    return (
        <div className={styles.colorPicker} ref={pickerRef}>
            {label && <label className={styles.label}>{label}</label>}

            <div className={styles.colorInput}>
                <div
                    className={`${styles.colorSwatch} ${disabled ? styles.disabled : ''}`}
                    style={{ backgroundColor: tempColor }}
                    onClick={handleSwatchClick}
                    title={`現在の色: ${tempColor}`}
                />

                <input
                    ref={inputRef}
                    type="text"
                    value={tempColor}
                    onChange={handleInputChange}
                    className={styles.hexInput}
                    placeholder="#000000"
                    disabled={disabled}
                    maxLength={7}
                />

                <input
                    type="color"
                    value={tempColor}
                    onChange={handleInputChange}
                    className={styles.nativeColorPicker}
                    disabled={disabled}
                />
            </div>

            {isOpen && !disabled && (
                <div className={styles.colorPickerPanel}>
                    <div className={styles.presetColors}>
                        <div className={styles.presetTitle}>プリセットカラー</div>
                        <div className={styles.presetGrid}>
                            {presetColors.map((presetColor, index) => (
                                <div
                                    key={index}
                                    className={`${styles.presetSwatch} ${tempColor.toLowerCase() === presetColor.toLowerCase() ? styles.selected : ''
                                        }`}
                                    style={{ backgroundColor: presetColor }}
                                    onClick={() => handleColorChange(presetColor)}
                                    title={presetColor}
                                />
                            ))}
                        </div>
                    </div>

                    <div className={styles.customColorSection}>
                        <div className={styles.customTitle}>カスタムカラー</div>
                        <div className={styles.colorInputs}>
                            <div className={styles.inputGroup}>
                                <label>HEX:</label>
                                <input
                                    type="text"
                                    value={tempColor}
                                    onChange={handleInputChange}
                                    className={styles.hexInputLarge}
                                    placeholder="#000000"
                                />
                            </div>

                            <div className={styles.rgbInputs}>
                                <div className={styles.inputGroup}>
                                    <label>R:</label>
                                    <input
                                        type="number"
                                        min="0"
                                        max="255"
                                        value={parseInt(tempColor.slice(1, 3), 16) || 0}
                                        onChange={(e) => {
                                            const r = Math.min(255, Math.max(0, parseInt(e.target.value) || 0));
                                            const g = parseInt(tempColor.slice(3, 5), 16) || 0;
                                            const b = parseInt(tempColor.slice(5, 7), 16) || 0;
                                            const newColor = `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
                                            handleColorChange(newColor);
                                        }}
                                        className={styles.rgbInput}
                                    />
                                </div>

                                <div className={styles.inputGroup}>
                                    <label>G:</label>
                                    <input
                                        type="number"
                                        min="0"
                                        max="255"
                                        value={parseInt(tempColor.slice(3, 5), 16) || 0}
                                        onChange={(e) => {
                                            const r = parseInt(tempColor.slice(1, 3), 16) || 0;
                                            const g = Math.min(255, Math.max(0, parseInt(e.target.value) || 0));
                                            const b = parseInt(tempColor.slice(5, 7), 16) || 0;
                                            const newColor = `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
                                            handleColorChange(newColor);
                                        }}
                                        className={styles.rgbInput}
                                    />
                                </div>

                                <div className={styles.inputGroup}>
                                    <label>B:</label>
                                    <input
                                        type="number"
                                        min="0"
                                        max="255"
                                        value={parseInt(tempColor.slice(5, 7), 16) || 0}
                                        onChange={(e) => {
                                            const r = parseInt(tempColor.slice(1, 3), 16) || 0;
                                            const g = parseInt(tempColor.slice(3, 5), 16) || 0;
                                            const b = Math.min(255, Math.max(0, parseInt(e.target.value) || 0));
                                            const newColor = `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
                                            handleColorChange(newColor);
                                        }}
                                        className={styles.rgbInput}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ColorPicker;
